import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2022 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbSDKRouter } from "abweb/service/router/absdkRouter/AbSDKRouter";
import { BootstrapBuiltinRouteProvider } from "abweb/service/router/BootstrapBuiltinRouteProvider";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { LogUtils } from "abweb/utils/log/LogUtils";

// Would be nice to put HealthApiFactory interface in HealthApi{Services}, but they are all generated classes
// (and below constructor is only place where this type is explicitly used)

class AbProductServiceIdentifier {
  constructor() {
    _defineProperty(this, "id", "");
    _defineProperty(this, "type", undefined);
  }
  get displayName() {
    try {
      return AbWebServices.sessionData.productInfo.name;
    } catch (e) {
      return "Primary Product Service";
    }
  }
}
export class AbWebServices {
  // null means NO services

  static resetServices() {
    if (AbWebServices.singleton) {
      // These are service implementations that need to unsubscribe else they have mem leaks!
      AbWebServices.singleton.clipboard?.destroy();
      this.resetRouter(true); // unsubscribe listeners
      AbWebServices.singleton.teardownService?.destroy();
    }
    AbWebServices.singleton = null;
  }

  /**
   * Used by USpec testing to reset the Router (if in use) between 'it' tests
   * (AbWebTestUtils.resetRouter invokes this private method!).
   * This is needed because if new Application objects are constructed in different tests,
   * they each add routes to the router which accumulate since the router is, by default, a singleton.
   * This method resets the Router to a new instance for a new test.
   */
  static resetRouter(suppressNewRouter = false) {
    const router = AbWebServices.singleton?.router;
    if (router) {
      switch (true) {
        case ClassUtils.isInstanceOf(router, AbSDKRouter):
          router.destroy();
          if (!suppressNewRouter) AbWebServices.singleton.router = new AbSDKRouter();
          break;
        default:
          throw new Error(`Unknown AbWeb.singleton.router instance type: ${router}`);
      }
    }
  }

  /**
   * Only application initialization error handling logic should ever need this to reason how far
   * app loading got before throwing.
   */
  static isInitialized() {
    return AbWebServices.singleton != null;
  }

  // Constructors: need cypress to be able to call empty constructor, and all other paths will call with all args.
  // See basart Mar 14 '16 comment in: https://stackoverflow.com/questions/35998629/typescript-constructor-overload-with-empty-constructor
  // where key line is 'you can pass none parameter or must pass *all* parameters.'
  // So overloads expect 0 or 20 args

  // Experiment below proving compilation errors if don't give 0 or all parsm
  // const fakeLocaleService: ILocaleService = undefined!;
  // const canNotJustUseOneArg = new AbWebServices(fakeLocaleService); // Error: overload expects 0 or 20 args

  // Empty constructor is a  test hook to make it easy to use I18N strings in tests (and by cypress)

  // This is effectively a private  constructor (overloads only call 0 or all arg constructors)
  // that above two constructors implicitly call
  constructor(localeService, i18NService, router, sessionData, rpcService, authenticationService, clientSessionService, productService, aboutService, imageIdentifierRegistry, imageIdentifierManager, dataSourceValueInfoService, packageForSupportService, clipboard, csrfTokenService, teardownService, notificationService, keyboardService, helpService, debugState, healthApiFactory, personaService, userNavigationService, userState, userSettingsService) {
    _defineProperty(this, "builtinRouteProvider", void 0);
    this.localeService = localeService;
    this.i18NService = i18NService;
    this.router = router;
    this.sessionData = sessionData;
    this.rpcService = rpcService;
    this.authenticationService = authenticationService;
    this.clientSessionService = clientSessionService;
    this.productService = productService;
    this.aboutService = aboutService;
    this.imageIdentifierRegistry = imageIdentifierRegistry;
    this.imageIdentifierManager = imageIdentifierManager;
    this.dataSourceValueInfoService = dataSourceValueInfoService;
    this.packageForSupportService = packageForSupportService;
    this.clipboard = clipboard;
    this.csrfTokenService = csrfTokenService;
    this.teardownService = teardownService;
    this.notificationService = notificationService;
    this.keyboardService = keyboardService;
    this.helpService = helpService;
    this.debugState = debugState;
    this.healthApiFactory = healthApiFactory;
    this.personaService = personaService;
    this.userNavigationService = userNavigationService;
    this.userState = userState;
    this.userSettingsService = userSettingsService;
    // Rudimentary implementation of BuiltinRouteProvider (home, logout) that will be
    // redefined later with route-based implementation after the NavigationService and router are started.
    // Prevents builtinRouteProvider callers from having to handle case of before/after router has started (e.g., banner)
    this.builtinRouteProvider = new BootstrapBuiltinRouteProvider();

    // If all services are defined
    if (localeService !== undefined && AbWebServices.singleton) {
      // Get hard to debug test failures if multiple AbWebService instances are live, so fail hard
      const errMsg = "Already initialized AbWebServices.  Call AbWebServices.resetServices before re-initializing.";
      AbWebServices.LOG.error(errMsg);
      throw new Error(errMsg);
    }
    AbWebServices.singleton = this;
  }

  // Test hook
  static setI18NServiceForTesting(i18nService) {
    AbWebServices.singleton.i18NService = i18nService;
  }
  static setPersonaServiceForTesting(personaService) {
    AbWebServices.singleton.personaService = personaService;
  }
  static setUserNavigationServiceForTesting(userNavigationService) {
    AbWebServices.singleton.userNavigationService = userNavigationService;
  }
  static setUserStateProviderServiceForTesting(userStateProvider) {
    AbWebServices.singleton.userState = userStateProvider;
  }
  static setUserSettingsServiceForTesting(userSettingsService) {
    AbWebServices.singleton.userSettingsService = userSettingsService;
  }
  static setRPCServiceForTesting(rpcService) {
    AbWebServices.singleton.rpcService = rpcService;
  }
  static setAuthenticationServiceForTesting(authenticationService) {
    AbWebServices.singleton.authenticationService = authenticationService;
  }
  static get localeService() {
    return AbWebServices.singleton.localeService;
  }
  static get i18n() {
    return AbWebServices.singleton.i18NService;
  }
  static get router() {
    return AbWebServices.singleton.router;
  }
  static get sessionData() {
    return AbWebServices.singleton.sessionData;
  }
  static get authenticationService() {
    return AbWebServices.singleton.authenticationService;
  }
  static get productService() {
    return AbWebServices.singleton.productService;
  }
  static get aboutService() {
    return AbWebServices.singleton.aboutService;
  }
  static get clientSessionService() {
    return AbWebServices.singleton.clientSessionService;
  }
  static get packageForSupportService() {
    return AbWebServices.singleton.packageForSupportService;
  }
  static get clipboard() {
    return AbWebServices.singleton.clipboard;
  }
  static getRPCService() {
    return AbWebServices.singleton.rpcService;
  }
  static csrfService() {
    return AbWebServices.singleton.csrfTokenService;
  }
  static teardownService() {
    return AbWebServices.singleton.teardownService;
  }
  static notificationService() {
    return AbWebServices.singleton.notificationService;
  }

  /**
   * Provide access to the AbWeb registry of images setup for the current install root. This is used
   * to obtain the image identifiers when they are being merged with custom sources, etc.
   *
   * @returns {AbWebImageIdentifierRegistry}
   */
  static getImageIdentifierRegistry() {
    return AbWebServices.singleton.imageIdentifierRegistry;
  }

  /**
   *
   * @returns {IImageIdentifierManager}
   */
  static getImageIdentifierManager() {
    return AbWebServices.singleton.imageIdentifierManager;
  }
  static get dataSourceValueInfoService() {
    return AbWebServices.singleton.dataSourceValueInfoService;
  }
  static get keyboardService() {
    return AbWebServices.singleton.keyboardService;
  }
  static get helpService() {
    return AbWebServices.singleton.helpService;
  }
  static get debugState() {
    return AbWebServices.singleton.debugState;
  }
  static get builtinRouteProvider() {
    return AbWebServices.singleton.builtinRouteProvider;
  }

  /**
   * Application will set a BuiltinRouteProvider whose implementation is based on the configured application router.
   * As the router is typically configured as part of the app module (last module configured), allow the app module
   * to 'set' the AbWebServices.builtinRouteProvider instance that is backed by their configured application router.
   * @param routeSpecificRouteProvider
   */
  // Note that the RTD router (NavigationService) sets the BuiltinRouteProvider from the NavigationService constructor.
  static set builtinRouteProvider(routeSpecificRouteProvider) {
    if (AbWebServices.singleton == null) {
      throw new Error("AbWebServices singleton is not yet created.");
    }
    AbWebServices.singleton.builtinRouteProvider = routeSpecificRouteProvider;
  }

  /**
   * Returns true if this is a development image.
   */
  static isDevelopmentImage() {
    return AbWebServices.sessionData.productInfo.version.indexOf("SNAPSHOT") > -1;
  }
  static getHealthApi(proxyLabel) {
    if (AbWebServices.singleton?.healthApiFactory == null) {
      throw new Error("AbWebServices singleton is not yet created, or AbWebServices.healthApiFactory has not yet been set.");
    }
    return AbWebServices.singleton.healthApiFactory(proxyLabel);
  }
  static get personaService() {
    return AbWebServices.singleton.personaService;
  }
  static get userNavigationService() {
    return AbWebServices.singleton.userNavigationService;
  }
  static get userState() {
    return AbWebServices.singleton.userState;
  }
  static get userSettingsService() {
    return AbWebServices.singleton.userSettingsService;
  }
}
_defineProperty(AbWebServices, "productServiceIdentifier", new AbProductServiceIdentifier());
_defineProperty(AbWebServices, "LOG", LogUtils.getLogger(__filename));
_defineProperty(AbWebServices, "singleton", void 0);
//# sourceMappingURL=AbWebServices.js.map
